// src/settings/components/macro/phrase-import.ts
//
// The rules behind the DEV-ONLY "Import phrases" dialog (spec §6.11 / §6.13): turn a pasted phrase
// library (Templates lane, `phrase-library-v1.json`) into CreateUpdateMacro bodies. Pure, so every
// rule is testable. The macro editor does not know pieces yet, so this is how piece-carrying phrases
// reach dev tonight.
//
//   VALIDATE  every definition: `macroKey` present; a `definitionDelta` (or bare ops); every
//             `cps-piece` run a v:1 shape the encounter codec accepts; and R1 — NO `chosen`,
//             `value` or `snapshot` ANYWHERE in the definition (a definition is not an occurrence).
//   WARN      R3 is the content owner's check; this only warns when the phrase's own text (outside a
//             pick-list) says "denies", "normal" or "no evidence of" — a pre-asserted finding.
//   CREATE ONLY  a key the owner already has (case-insensitive) is skipped as "exists"; nothing is
//             overwritten or deleted. A key repeated in the paste is imported once.
//   ENCODE    `macroValue` = the definition Delta with every `cps-piece` value as a JSON STRING (the
//             same encoding the encounter saves notes in); `macroValueFormatted` = the plain-text
//             export (open pieces read `[not completed: <label>]`).

import type { SaveMacroBody } from '@/settings/services/use-save-macro';
import type { DeltaOp } from '@/encounter/lib/note-delta';
import { PIECE_ATTR, exportPlainText, labSetId, labTestId, occurrenceFieldsIn, parsePiece, pieceDisplay } from '@/encounter/lib/note-pieces';
import { labSet, labTest } from '@/encounter/lib/lab-set-catalogue';
import { PHRASE_ATTR } from '@/encounter/lib/smart-phrase';

export interface PhraseDefinitionInput {
  macroKey?: unknown;
  scope?: unknown;
  definitionDelta?: unknown;
  [k: string]: unknown;
}

export type ImportCheck =
  | { ok: true; macroKey: string; ops: DeltaOp[]; plain: string; isGlobal: boolean; pieces: number; warnings: string[] }
  | { ok: false; macroKey?: string; reason: string };

const OCCURRENCE_KEYS = ['chosen', 'value', 'snapshot'];
/** A phrase title longer than this is cut (a menu row is one line). */
const TITLE_MAX = 120;
const R3_WORDS = /\b(denies|normal|no evidence of)\b/i;

/** Pasted text → definitions. Accepts a bare array or the library envelope `{ phrases: [...] }`. */
export function parsePhraseLibrary(text: string): { items: PhraseDefinitionInput[] } | { error: string } {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { error: 'That is not valid JSON.' };
  }
  const items = Array.isArray(data) ? data : (data as { phrases?: unknown })?.phrases;
  if (!Array.isArray(items)) return { error: 'Expected an array of phrase definitions (or a library with "phrases").' };
  return { items: items as PhraseDefinitionInput[] };
}

/** Every path in `value` whose key is an occurrence-only field (R1). */
function occurrencePaths(value: unknown, path = ''): string[] {
  if (Array.isArray(value)) return value.flatMap((v, i) => occurrencePaths(v, `${path}[${i}]`));
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) => [
    ...(OCCURRENCE_KEYS.includes(k) ? [`${path ? `${path}.` : ''}${k}`] : []),
    ...occurrencePaths(v, path ? `${path}.${k}` : k),
  ]);
}

export function checkDefinition(item: PhraseDefinitionInput): ImportCheck {
  const macroKey = typeof item.macroKey === 'string' ? item.macroKey.trim() : '';
  if (!macroKey) return { ok: false, reason: 'no macroKey' };

  const r1 = occurrencePaths(item);
  if (r1.length) return { ok: false, macroKey, reason: `a definition may not carry chosen, value or snapshot (R1): ${r1.join(', ')}` };

  const delta = item.definitionDelta;
  const rawOps = Array.isArray(delta) ? delta : (delta as { ops?: unknown })?.ops;
  if (!Array.isArray(rawOps) || !rawOps.length) return { ok: false, macroKey, reason: 'no definitionDelta ops' };

  const ops: DeltaOp[] = [];
  let pieces = 0;
  let narrative = '';
  for (const [i, op] of (rawOps as DeltaOp[]).entries()) {
    if (!op || (typeof op.insert !== 'string' && (typeof op.insert !== 'object' || op.insert === null))) {
      return { ok: false, macroKey, reason: `op ${i} has no insert` };
    }
    // The wire carries text runs and images only. Any other embed — above all an editor-only
    // piece embed — would break the note in flutter_quill 10.7.6, which throws on an unknown embed.
    const embedKeys = typeof op.insert === 'string' ? [] : Object.keys(op.insert);
    const isImage = embedKeys.length === 1 && embedKeys[0] === 'image' && typeof (op.insert as { image?: unknown }).image === 'string';
    if (typeof op.insert !== 'string' && !isImage) {
      return { ok: false, macroKey, reason: `op ${i}: only text and images may be stored (found an embed: ${Object.keys(op.insert).join(', ') || 'empty'})` };
    }
    const raw = op.attributes?.[PIECE_ATTR];
    if (raw === undefined) {
      if (typeof op.insert === 'string') narrative += op.insert;
      ops.push(op);
      continue;
    }
    const piece = parsePiece(raw);
    if (!piece) return { ok: false, macroKey, reason: `op ${i}: not a v:1 pick / blank / chart piece this editor supports` };
    // R1 again on the PARSED piece: a JSON-string attribute is opaque to the object walk above.
    const carried = occurrenceFieldsIn(piece as unknown as Record<string, unknown>);
    if (carried.length) {
      return { ok: false, macroKey, reason: `a definition may not carry chosen, value or snapshot (R1): op ${i} ${carried.join(', ')}` };
    }
    if (typeof op.insert !== 'string') return { ok: false, macroKey, reason: `op ${i}: a piece must be a text run` };
    // An author may only name a lab set or test this catalogue defines (the encounter would quote nothing).
    const setId = piece.t === 'chart' ? labSetId(piece.query) : undefined;
    if (setId !== undefined && !labSet(setId)) return { ok: false, macroKey, reason: `op ${i}: no lab set "${setId}" in the catalogue` };
    const testId = piece.t === 'chart' ? labTestId(piece.query) : undefined;
    if (testId !== undefined && !labTest(testId)) return { ok: false, macroKey, reason: `op ${i}: no lab test "${testId}" in the catalogue` };
    pieces++;
    // The run text is the piece's display — for a definition, `[not completed: <label>]`.
    ops.push({ insert: pieceDisplay(piece), attributes: { ...op.attributes, [PIECE_ATTR]: JSON.stringify(piece) } });
  }
  const last = ops[ops.length - 1];
  if (typeof last.insert !== 'string' || !last.insert.endsWith('\n')) ops.push({ insert: '\n' });

  // Which notes the phrase is for → `cps-phrase` on the terminal newline, so the encounter offers
  // it only there (a chief-complaint phrase never appears in the A&P narrative). PROD-3149: the
  // library's own `title` rides there too, so menus name the phrase by it, not by its code.
  const editors = item.editors;
  if (editors !== undefined && (!Array.isArray(editors) || !editors.length || !editors.every((e) => typeof e === 'string' && e.trim()))) {
    return { ok: false, macroKey, reason: 'editors must be a non-empty list of note names' };
  }
  const title = typeof item.title === 'string' && item.title.trim() ? item.title.trim().slice(0, TITLE_MAX) : undefined;
  if (editors !== undefined || title) {
    const end = ops[ops.length - 1];
    const text = end.insert as string;
    const meta = { [PHRASE_ATTR]: JSON.stringify({ v: 1, ...(editors !== undefined ? { editors } : {}), ...(title ? { title } : {}) }) };
    if (text === '\n') ops[ops.length - 1] = { ...end, attributes: { ...(end.attributes ?? {}), ...meta } };
    else ops.splice(ops.length - 1, 1, { ...end, insert: text.slice(0, -1) }, { insert: '\n', attributes: meta });
  }

  const warnings = R3_WORDS.test(narrative)
    ? [`text outside a pick-list says "${R3_WORDS.exec(narrative)?.[0]}" — a pre-asserted finding? (R3, the content owner's check)`]
    : [];
  return {
    ok: true,
    macroKey,
    ops,
    plain: exportPlainText(ops),
    isGlobal: item.scope === 'shared' || item.scope === 'global',
    pieces,
    warnings,
  };
}

export interface ImportPlan {
  toSave: Array<{ macroKey: string; body: SaveMacroBody; warnings: string[] }>;
  skipped: Array<{ macroKey: string; reason: string }>;
}

/** Validate every definition and plan the CREATE-ONLY save against the owner's existing keys. */
export function planPhraseImport(
  items: PhraseDefinitionInput[],
  existingKeys: string[],
  owner: { ownerId: string; organizationId: string },
): ImportPlan {
  const taken = new Set(existingKeys.map((k) => k.trim().toLowerCase()));
  const plan: ImportPlan = { toSave: [], skipped: [] };
  items.forEach((item, i) => {
    const check = checkDefinition(item);
    if (!check.ok) {
      plan.skipped.push({ macroKey: check.macroKey ?? `#${i + 1}`, reason: check.reason });
      return;
    }
    const key = check.macroKey.toLowerCase();
    if (taken.has(key)) {
      plan.skipped.push({ macroKey: check.macroKey, reason: 'exists' });
      return;
    }
    taken.add(key);
    plan.toSave.push({
      macroKey: check.macroKey,
      warnings: check.warnings,
      body: {
        id: null,
        organizationId: owner.organizationId,
        ownerId: owner.ownerId,
        macroKey: check.macroKey,
        macroValue: JSON.stringify(check.ops),
        macroValueFormatted: check.plain,
        isGlobal: check.isGlobal,
        isDeleted: false,
        macroCollaborators: [],
      },
    });
  });
  return plan;
}
